import { useEffect, useRef, useCallback, useState } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import * as Sentry from '@sentry/react';

/**
 * Custom hook for real-time Kanban board updates via SignalR
 * Manages connection to ActionItemBoardHub and handles board-level events
 * 
 * @param {number} boardId - The ID of the board to subscribe to
 * @param {Object} callbacks - Event callbacks for different board events
 * @param {Function} callbacks.onBoardUpdated - Called when board settings change
 * @param {Function} callbacks.onColumnsChanged - Called when columns are added/removed/reordered
 * @param {Function} callbacks.onItemAdded - Called when an item is added to the board
 * @param {Function} callbacks.onItemUpdated - Called when an item on the board is updated
 * @param {Function} callbacks.onItemMoved - Called when an item is moved to/from this board
 * @param {Function} callbacks.onItemDeleted - Called when an item is deleted from the board
 * @param {Function} callbacks.onAccessGranted - Called when access is granted to a user
 * @param {Function} callbacks.onAccessRevoked - Called when access is revoked from a user
 * @param {Function} callbacks.onBoardListChanged - Called when the user's board list changes (access granted/revoked elsewhere)
 * @param {Function} callbacks.onUserJoined - Called when another user joins the board
 * @param {Function} callbacks.onUserLeft - Called when another user leaves the board
 * @param {boolean} enabled - Whether the connection should be active
 * @returns {Object} - { connection, isConnected, broadcastBoardUpdate, broadcastColumnsChange, etc. }
 */
const useActionItemBoardSignalR = (boardId, callbacks = {}, enabled = true) => {
    const connectionRef = useRef(null);
    const isConnectedRef = useRef(false);
    const reconnectTimeoutRef = useRef(null);
    const startConnectionRef = useRef(null);
    const callbacksRef = useRef(callbacks);
    const [isConnected, setIsConnected] = useState(false);

    // Keep callbacksRef in sync so event handlers always invoke the latest
    // callbacks without requiring a SignalR reconnection.
    useEffect(() => {
        callbacksRef.current = callbacks;
    }, [callbacks]);

    /**
     * Checks whether a JWT token string is expired
     * @param {string} token - The JWT token to check
     * @returns {boolean} true if the token is expired or unparseable
     */
    const isTokenExpired = useCallback((token) => {
        try {
            if (!token) return true;
            const payload = JSON.parse(atob(token.split('.')[1]));
            return Date.now() >= payload.exp * 1000;
        } catch (catchError) {
            Sentry.captureException(catchError);
            return true;
        }
    }, []);

    /**
     * Build and start the SignalR connection
     */
    const startConnection = useCallback(async () => {
        if (!enabled || !boardId || isConnectedRef.current) {
            return;
        }

        try {
            // Get access token from localStorage
            // Check multiple possible storage locations for the token
            const storedUser = localStorage.getItem('user');
            const token = storedUser ? (JSON.parse(storedUser)?.token ?? null) : null;

            if (!token) {
                console.debug('[SignalR Board] No access token available for SignalR connection');
                return;
            }

            if (isTokenExpired(token)) {
                console.debug('[SignalR Board] Token expired — skipping SignalR connection');
                return;
            }

            // Build connection with JWT authentication.
            // Hub path is /api/hubs/actionitemboard so that the production Nginx /api/
            // location block (which carries the WebSocket upgrade headers) forwards the
            // request to the backend.  /hubs/ at the root would resolve to the static-file
            // server and return 404.
            const connection = new HubConnectionBuilder()
                .withUrl(`/api/hubs/actionitemboard`, {
                    accessTokenFactory: () => token
                })
                .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: (retryContext) => {
                        // Exponential backoff: 0s, 2s, 10s, 30s, then 60s
                        if (retryContext.previousRetryCount === 0) return 0;
                        if (retryContext.previousRetryCount === 1) return 2000;
                        if (retryContext.previousRetryCount === 2) return 10000;
                        if (retryContext.previousRetryCount === 3) return 30000;
                        return 60000;
                    }
                })
                .configureLogging(LogLevel.Information)
                .build();

            // Set up event handlers
            connection.on('BoardUpdated', (data) => {
                console.log('BoardUpdated:', data);
                callbacksRef.current.onBoardUpdated?.(data);
            });

            connection.on('ColumnsChanged', (data) => {
                console.log('ColumnsChanged:', data);
                callbacksRef.current.onColumnsChanged?.(data);
            });

            connection.on('ItemAdded', (data) => {
                console.log('ItemAdded:', data);
                callbacksRef.current.onItemAdded?.(data);
            });

            connection.on('ItemUpdated', (data) => {
                console.log('ItemUpdated:', data);
                callbacksRef.current.onItemUpdated?.(data);
            });

            connection.on('ItemAddedToBoard', (data) => {
                console.log('ItemAddedToBoard:', data);
                callbacksRef.current.onItemMoved?.(data);
            });

            connection.on('ItemRemovedFromBoard', (data) => {
                console.log('ItemRemovedFromBoard:', data);
                callbacksRef.current.onItemMoved?.(data);
            });

            connection.on('ItemDeleted', (data) => {
                console.log('ItemDeleted:', data);
                callbacksRef.current.onItemDeleted?.(data);
            });

            connection.on('AccessGranted', (data) => {
                console.log('AccessGranted:', data);
                callbacksRef.current.onAccessGranted?.(data);
            });

            connection.on('AccessRevoked', (data) => {
                console.log('AccessRevoked:', data);
                callbacksRef.current.onAccessRevoked?.(data);
            });

            connection.on('BoardListChanged', (data) => {
                console.log('BoardListChanged:', data);
                callbacksRef.current.onBoardListChanged?.(data);
            });

            connection.on('UserJoinedBoard', (data) => {
                // UserJoinedBoard event received (user info not logged to prevent cleartext logging)
                callbacksRef.current.onUserJoined?.(data);
            });

            connection.on('UserLeftBoard', (data) => {
                // UserLeftBoard event received (user info not logged to prevent cleartext logging)
                callbacksRef.current.onUserLeft?.(data);
            });

            connection.on('DuplicatesChanged', (data) => {
                // Use console.debug so this is hidden in production devtools by default
                // and only visible when the verbose/debug log level is enabled.
                if (import.meta.env.DEV) console.debug('[SignalR Board] DuplicatesChanged:', data);
                callbacksRef.current.onDuplicatesChanged?.(data);
            });

            // Handle reconnection events
            connection.onreconnecting((error) => {
                console.warn('SignalR reconnecting...', error);
                isConnectedRef.current = false;
                setIsConnected(false);
            });

            connection.onreconnected((connectionId) => {
                console.log('SignalR reconnected:', connectionId);
                isConnectedRef.current = true;
                setIsConnected(true);
                // Rejoin the board group after reconnection
                connection.invoke('JoinBoard', boardId).catch(err => {
                    console.error('Error rejoining board after reconnect:', err);
                });
            });

            connection.onclose((error) => {
                console.log('SignalR connection closed', error);
                isConnectedRef.current = false;
                setIsConnected(false);

                // Attempt to reconnect after a delay if connection was closed unexpectedly
                // but NOT for authentication errors (those require re-login)
                if (error && enabled) {
                    const errMsg = error?.message || error?.toString() || '';
                    const isAuthError = errMsg.includes('401') || errMsg.includes('Token expired') || errMsg.includes('Unauthorized');
                    if (!isAuthError) {
                        reconnectTimeoutRef.current = setTimeout(() => {
                            startConnectionRef.current?.();
                        }, 5000);
                    }
                }
            });

            // Start the connection
            await connection.start();
            console.log('SignalR connected to ActionItemBoardHub');
            isConnectedRef.current = true;
            setIsConnected(true);

            // Join the board group
            await connection.invoke('JoinBoard', boardId);
            console.log(`Joined board ${boardId} SignalR group`);

            connectionRef.current = connection;
        } catch (error) {
            Sentry.captureException(error);
            // Detect 401 / token-expired errors and handle them silently
            // — the auth layer will redirect to login
            const errMsg = error?.message || error?.toString() || '';
            const isAuthError = errMsg.includes('Status code \'401\'') || errMsg.includes('Token expired') || errMsg.includes('Unauthorized');

            if (isAuthError) {
                console.debug('[SignalR Board] Connection skipped — session expired or unauthorized');
            } else {
                console.error('Error starting SignalR connection:', error);
            }
            isConnectedRef.current = false;

            // Only retry for non-auth errors; auth failures will be resolved by re-login
            if (!isAuthError && enabled) {
                reconnectTimeoutRef.current = setTimeout(() => {
                    startConnectionRef.current?.();
                }, 5000);
            }
        }
    // callbacks intentionally omitted — callbacksRef keeps handlers fresh
    // without restarting the SignalR connection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boardId, enabled, isTokenExpired]);

    /**
     * Stop the SignalR connection
     */
    const stopConnection = useCallback(async () => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (connectionRef.current) {
            try {
                // Leave the board group before disconnecting
                if (isConnectedRef.current && boardId) {
                    await connectionRef.current.invoke('LeaveBoard', boardId);
                }
                
                await connectionRef.current.stop();
                console.log('SignalR connection stopped');
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error stopping SignalR connection:', error);
            } finally {
                connectionRef.current = null;
                isConnectedRef.current = false;
                setIsConnected(false);
            }
        }
    }, [boardId]);

    /**
     * Broadcast helper functions - can be called by the consuming component
     */
    const broadcastBoardUpdate = useCallback(async (changeType, changes) => {
        if (connectionRef.current && isConnectedRef.current) {
            try {
                await connectionRef.current.invoke('NotifyBoardUpdated', boardId, changeType, changes);
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error broadcasting board update:', error);
            }
        }
    }, [boardId]);

    const broadcastColumnsChange = useCallback(async (action, columnData) => {
        if (connectionRef.current && isConnectedRef.current) {
            try {
                await connectionRef.current.invoke('NotifyColumnsChanged', boardId, action, columnData);
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error broadcasting columns change:', error);
            }
        }
    }, [boardId]);

    const broadcastItemAdded = useCallback(async (itemId, itemData) => {
        if (connectionRef.current && isConnectedRef.current) {
            try {
                await connectionRef.current.invoke('NotifyItemAdded', boardId, itemId, itemData);
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error broadcasting item added:', error);
            }
        }
    }, [boardId]);

    const broadcastItemUpdated = useCallback(async (itemId, changeType, changes) => {
        if (connectionRef.current && isConnectedRef.current) {
            try {
                await connectionRef.current.invoke('NotifyItemUpdated', boardId, itemId, changeType, changes);
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error broadcasting item updated:', error);
            }
        }
    }, [boardId]);

    const broadcastItemMoved = useCallback(async (fromBoardId, toBoardId, itemId, itemData) => {
        if (connectionRef.current && isConnectedRef.current) {
            try {
                await connectionRef.current.invoke('NotifyItemMoved', fromBoardId, toBoardId, itemId, itemData);
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error broadcasting item moved:', error);
            }
        }
    }, []);

    const broadcastItemDeleted = useCallback(async (itemId) => {
        if (connectionRef.current && isConnectedRef.current) {
            try {
                await connectionRef.current.invoke('NotifyItemDeleted', boardId, itemId);
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error broadcasting item deleted:', error);
            }
        }
    }, [boardId]);

    const broadcastAccessGranted = useCallback(async (grantedToUserId, permissionLevel) => {
        if (connectionRef.current && isConnectedRef.current) {
            try {
                await connectionRef.current.invoke('NotifyAccessGranted', boardId, grantedToUserId, permissionLevel);
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error broadcasting access granted:', error);
            }
        }
    }, [boardId]);

    const broadcastAccessRevoked = useCallback(async (revokedFromUserId) => {
        if (connectionRef.current && isConnectedRef.current) {
            try {
                await connectionRef.current.invoke('NotifyAccessRevoked', boardId, revokedFromUserId);
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error broadcasting access revoked:', error);
            }
        }
    }, [boardId]);

    // Keep ref in sync with latest startConnection inside an effect (not during render)
    useEffect(() => {
        startConnectionRef.current = startConnection;
    }, [startConnection]);

    // Start connection when hook mounts or boardId changes
    // Note: callbacks should be memoized by the consuming component to avoid re-renders
    useEffect(() => {
        (async () => {
            if (enabled && boardId) {
                await startConnection();
            }
        })();

        return () => {
            stopConnection();
        };
    }, [boardId, enabled, startConnection, stopConnection]);

    return {
        isConnected,
        broadcastBoardUpdate,
        broadcastColumnsChange,
        broadcastItemAdded,
        broadcastItemUpdated,
        broadcastItemMoved,
        broadcastItemDeleted,
        broadcastAccessGranted,
        broadcastAccessRevoked
    };
};

export default useActionItemBoardSignalR;
